import axios from 'axios';
import { 
  ApiResponse, 
  PoolCreate, 
  PoolUpdate
} from '../types/api';
import { Pool, SafetyStatus, PoolFilter } from '../types/domain';
import { API_URL, handleApiResponse, logApiError } from '../utils/api';
import { hashSHA256 } from '../utils/hashUtils';

/**
 * 풀 관리를 위한 표준화된 API 모듈
 * URL 경로에는 내부적으로 pool_id로 변환하여 사용합니다.
 */
const standardPoolApi = {
  /**
   * 모든 풀 정보를 가져옵니다.
   * @param filter 선택적 필터링 옵션
   * @returns 풀 목록
   */
  async getAllPools(filter?: PoolFilter): Promise<ApiResponse<Pool[]>> {
    try {
      console.log('모든 풀 정보 요청', filter ? { filter } : '필터 없음');
      
      let url = '/api/pools/';
      const queryParams = new URLSearchParams();
      if (filter?.status) queryParams.append('status', filter.status);
      if (filter?.location) queryParams.append('location', filter.location);
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }
      
      const response = await axios.get(url);
      return handleApiResponse<Pool[]>(response);
    } catch (error) {
      return logApiError<Pool[]>('풀 목록 조회 중 오류 발생', error);
    }
  },

  async getPools(params?: { search?: string; sort?: string; order?: string; page?: number; page_size?: number }): Promise<ApiResponse<Pool[]>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append('search', params.search);
      if (params?.sort) queryParams.append('sort', params.sort);
      if (params?.order) queryParams.append('order', params.order);
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.page_size) queryParams.append('page_size', String(params.page_size));

      const url = `/api/pools/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axios.get(url);
      const result = handleApiResponse<Pool[]>(response);
      return result;
    } catch (error) {
      return logApiError<Pool[]>('풀 목록 조회 중 오류 발생', error);
    }
  },
  
  /**
   * 풀 코드를 사용하여 풀 정보를 가져옵니다.
   * 주의: 이 함수는 URL에서 pool_code 사용 시에만 사용하고, 이후에는 getPoolById 사용
   * @param pool_code 풀 코드
   * @returns 풀 정보
   */
  async getPoolByCode(pool_code: string): Promise<ApiResponse<Pool>> {
    try {
      console.log(`[DEPRECATED] 풀 코드로 정보 요청: ${pool_code} - ID 변환 목적으로만 사용`);
      const response = await axios.get(`${API_URL}/api/pools/code/${pool_code}`);
      return handleApiResponse<Pool>(response);
    } catch (error) {
      return logApiError<Pool>(`풀 코드(${pool_code}) 정보 조회 중 오류 발생`, error);
    }
  },
  
  /**
   * 풀 ID를 사용하여 풀 정보를 가져옵니다.
   * @param pool_id 풀 ID
   * @returns 풀 정보
   */
  async getPoolById(pool_id: number | string): Promise<ApiResponse<Pool>> {
    try {
      // QueryString 파라미터 추출 (캐시 무효화 타임스탬프 등)
      const poolIdStr = String(pool_id);
      const hasQueryParams = poolIdStr.includes('?');
      const cleanId = hasQueryParams ? poolIdStr.split('?')[0] : poolIdStr;
      const queryParams = hasQueryParams ? `?${poolIdStr.split('?')[1]}` : '';
      
      console.log(`풀 ID로 정보 요청: ${cleanId}${queryParams ? ' (캐시 무효화 파라미터 포함)' : ''}`);
      const response = await axios.get(`${API_URL}/api/pools/${cleanId}${queryParams}`);
      const result = handleApiResponse<Pool>(response);
      console.log(`풀 ID(${cleanId}) 정보 응답:`, result.data);
      return result;
    } catch (error) {
      return logApiError<Pool>(`풀 ID(${pool_id}) 정보 조회 중 오류 발생`, error);
    }
  },
  
  /**
   * 새 풀을 생성합니다.
   * @param poolData 생성할 풀 데이터
   * @returns 생성된 풀 정보
   */
  async createPool(poolData: PoolCreate): Promise<ApiResponse<Pool>> {
    try {
      console.log('풀 생성 요청:', poolData);
      const response = await axios.post(`${API_URL}/api/pools`, poolData);
      return handleApiResponse<Pool>(response);
    } catch (error) {
      return logApiError<Pool>('풀 생성 중 오류 발생', error);
    }
  },
  
  /**
   * 풀 ID를 사용하여 풀 정보를 업데이트합니다.
   * @param pool_id 풀 ID
   * @param poolData 업데이트할 풀 데이터
   * @returns 업데이트된 풀 정보
   */
  async updatePoolById(pool_id: number | string, poolData: PoolUpdate): Promise<ApiResponse<Pool>> {
    try {
      console.log(`풀 ID(${pool_id}) 정보 업데이트 요청:`, poolData);
      
      // pool_id가 문자열이고 숫자로 변환할 수 있는지 확인
      let updatedPoolId = pool_id;
      if (typeof pool_id === 'string') {
        // 풀 코드(문자열)인 경우, 해당 코드로 풀 정보를 가져와서 ID를 추출
        console.log(`풀 코드 감지됨. 풀 ID로 변환 시도: ${pool_id}`);
        try {
          const poolInfoResponse = await this.getPoolByCode(pool_id);
          if (poolInfoResponse.success && poolInfoResponse.data && poolInfoResponse.data.id) {
            updatedPoolId = poolInfoResponse.data.id;
            console.log(`풀 코드(${pool_id})가 풀 ID(${updatedPoolId})로 변환되었습니다.`);
          } else {
            console.warn(`풀 코드(${pool_id})로 풀 정보를 가져오지 못했습니다.`);
          }
        } catch (err) {
          console.error(`풀 코드(${pool_id})를 ID로 변환하는데 실패했습니다.`, err);
          // 변환 실패 시 원래 ID를 사용
        }
      }
      
      console.log(`업데이트 요청 URL: ${API_URL}/api/pools/${updatedPoolId}`);
      
      // 비밀번호 필드가 있는지 확인하고 로깅
      if (poolData.password) {
        console.log(`비밀번호 업데이트 포함됨 (길이: ${poolData.password.length})`);
      }
      
      // 요청 전 데이터 상태 필드 유효성 검사 및 수정
      let backendPoolData = { ...poolData };
      
      // 배열 필드 처리
      const arrayFields = ['cctv_ips', 'monitor_ips', 'sirens', 'buzzers', 'external_photos', 'internal_photos'];
      
      // 현재 데이터 로깅
      console.log('업데이트 전 데이터:', {
        original: poolData,
        arrayFields: arrayFields.map(field => ({
          field,
          value: poolData[field],
          type: typeof poolData[field]
        }))
      });

      // 배열 필드 초기화
      arrayFields.forEach(field => {
        // 필드가 undefined이거나 null이거나 빈 배열이면 빈 배열로 설정
        if (!poolData[field] || 
            (Array.isArray(poolData[field]) && poolData[field].length === 0) ||
            (typeof poolData[field] === 'string' && poolData[field].trim() === '')) {
          backendPoolData[field] = [];
          console.log(`필드 ${field}를 빈 배열로 초기화`);
        } else {
          // 배열이 아닌 경우 배열로 변환
          if (!Array.isArray(poolData[field])) {
            backendPoolData[field] = [poolData[field]];
            console.log(`필드 ${field}를 배열로 변환:`, backendPoolData[field]);
          }
        }
      });

      // 최종 데이터 로깅
      console.log('업데이트할 최종 데이터:', {
        final: backendPoolData,
        arrayFields: arrayFields.map(field => ({
          field,
          value: backendPoolData[field],
          type: typeof backendPoolData[field]
        }))
      });
      
      // 기존 데이터를 가져와서 현재 데이터와 병합
      try {
        const currentPoolData = await this.getPoolById(updatedPoolId);
        if (currentPoolData.success && currentPoolData.data) {
          const mergedData = { ...currentPoolData.data };
          // 배열 필드들은 현재 데이터로 완전히 대체
          arrayFields.forEach(field => {
            if (backendPoolData[field] !== undefined) {
              mergedData[field] = backendPoolData[field];
            }
          });
          // 나머지 필드는 현재 데이터로 업데이트
          Object.assign(mergedData, backendPoolData);
          backendPoolData = mergedData;
        }
      } catch (err) {
        console.warn('기존 풀 데이터를 가져오는데 실패했습니다:', err);
      }
      
      const requestPayload: Record<string, unknown> = { ...backendPoolData };
      // 백엔드 API에서 요구하는 상태값으로 변환
      if (backendPoolData.status) {
        console.log(`원본 상태값: ${backendPoolData.status}`);
        const currentStatus = String(backendPoolData.status);
        let normalizedStatus = currentStatus;
        if (currentStatus === 'active') {
          normalizedStatus = 'OPERATING';
          console.log('상태 필드 변환: active -> OPERATING');
        } else if (currentStatus === 'inactive') {
          normalizedStatus = 'CLOSED';
          console.log('상태 필드 변환: inactive -> CLOSED');
        } else if (currentStatus === 'maintenance') {
          normalizedStatus = 'MAINTENANCE';
          console.log('상태 필드 변환: maintenance -> MAINTENANCE');
        }

        const validStatuses = ['OPERATING', 'CLOSED', 'MAINTENANCE'];
        if (!validStatuses.includes(normalizedStatus)) {
          console.warn(`유효하지 않은 상태값: ${normalizedStatus}. 기본값인 OPERATING으로 설정됩니다.`);
          normalizedStatus = 'OPERATING';
        }
        requestPayload.status = normalizedStatus;
      }
      
      // HTTP PUT 요청 전송
      const response = await axios.put(`${API_URL}/api/pools/${updatedPoolId}`, requestPayload);
      
      // 응답 상세 정보 로깅
      console.log(`풀 ID(${updatedPoolId}) 정보 업데이트 응답 상태:`, response.status);
      console.log(`풀 ID(${updatedPoolId}) 정보 업데이트 응답 헤더:`, response.headers);
      
      // 응답 본문 전체 로깅 (민감 정보는 제외)
      const sanitizedResponseData = response.data ? { ...response.data } : null;
      if (sanitizedResponseData && sanitizedResponseData.data) {
        // 비밀번호 필드 삭제
        if (sanitizedResponseData.data.password) sanitizedResponseData.data.password = '[REDACTED]';
      }
      console.log(`풀 ID(${updatedPoolId}) 정보 업데이트 응답 본문:`, sanitizedResponseData);
      
      // 응답 처리
      const apiResponse = handleApiResponse<Pool>(response);
      console.log(`풀 ID(${updatedPoolId}) 정보 업데이트 처리된 결과:`, 
        apiResponse.success ? '성공' : '실패', 
        apiResponse.message
      );
      
      return apiResponse;
    } catch (error) {
      console.error(`풀 ID(${pool_id}) 정보 업데이트 중 오류 발생`, error);
      const errorObj = error as {
        message?: string;
        response?: {
          status?: number;
          statusText?: string;
          data?: unknown;
        };
      };
      
      // 상세 오류 정보 로깅
      console.error('오류 세부 정보:', {
        message: errorObj.message,
        status: errorObj.response?.status,
        statusText: errorObj.response?.statusText,
        data: errorObj.response?.data
      });
      
      const errorResponse = logApiError<Pool>(`풀 ID(${pool_id}) 정보 업데이트 중 오류 발생`, error);
      console.log(`풀 ID(${pool_id}) 정보 업데이트 오류 응답:`, errorResponse);
      return errorResponse;
    }
  },
  
  /**
   * 풀을 삭제합니다. ID 또는 코드를 사용할 수 있습니다.
   * @param identifier 풀 ID 또는 코드
   * @returns 삭제 결과
   */
  async deletePool(identifier: number | string): Promise<ApiResponse<void>> {
    try {
      console.log(`[standardPoolApi] 풀 삭제 시작 - 식별자:`, identifier);
      
      let poolId = identifier;
      
      // 문자열(코드)인 경우 ID로 변환
      if (typeof identifier === 'string' && isNaN(Number(identifier))) {
        console.log(`[standardPoolApi] 코드를 ID로 변환 시도:`, identifier);
        const poolInfo = await this.getPoolByCode(identifier);
        if (!poolInfo.success || !poolInfo.data?.id) {
          throw new Error(`풀 코드(${identifier})에 해당하는 ID를 찾기 불가`);
        }
        poolId = poolInfo.data.id;
        console.log(`[standardPoolApi] 코드(${identifier})가 ID(${poolId})로 변환됨`);
      }

      console.log(`[standardPoolApi] 삭제 요청 - ID:`, poolId);
      
      try {
        const response = await axios.delete(`${API_URL}/api/pools/${poolId}`);
        console.log(`[standardPoolApi] 삭제 API 응답:`, {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          data: response.data
        });
        
        // 204 No Content는 성공적인 삭제를 의미
        if (response.status === 204) {
          console.log(`[standardPoolApi] 풀 삭제 성공 (204 No Content) - ID:`, poolId);
          return {
            success: true,
            data: null,
            message: '시설이 삭제 완료'
          };
        }
        
        // 200 OK와 다른 성공 상태 코드 처리
        if (response.status >= 200 && response.status < 300) {
          console.log(`[standardPoolApi] 풀 삭제 성공 (${response.status}) - ID:`, poolId);
          return handleApiResponse<void>(response);
        }

        // 예상치 못한 상태 코드
        console.warn(`[standardPoolApi] 예상치 못한 응답 상태 코드: ${response.status}`);
        return {
          success: false,
          data: null,
          message: `삭제 요청에 대한 예상치 못한 응답: ${response.status} ${response.statusText}`
        };
      } catch (axiosError: any) {
        console.error(`[standardPoolApi] 삭제 요청 실패:`, {
          status: axiosError.response?.status,
          statusText: axiosError.response?.statusText,
          data: axiosError.response?.data,
          message: axiosError.message
        });
        
        if (axiosError.response?.status === 404) {
          return {
            success: false,
            data: null,
            message: '삭제하려는 시설을 찾기 불가'
          };
        }
        
        return {
          success: false,
          data: null,
          message: axiosError.response?.data?.message || axiosError.message || '시설 삭제 중 오류 발생'
        };
      }
    } catch (error: any) {
      console.error(`[standardPoolApi] 풀 삭제 중 오류:`, error);
      return {
        success: false,
        data: null,
        message: error.message || '시설 삭제 중 오류 발생'
      };
    }
  },
  
  /**
   * 특정 타입의 풀 목록을 가져옵니다.
   * @param type 풀 타입
   * @returns 특정 타입의 풀 목록
   */
  async getPoolsByType(type: string): Promise<ApiResponse<Pool[]>> {
    try {
      console.log(`타입별 풀 목록 요청: ${type}`);
      const response = await axios.get(`${API_URL}/api/pools?type=${type}`);
      return handleApiResponse<Pool[]>(response);
    } catch (error) {
      return logApiError<Pool[]>(`${type} 타입 풀 목록 조회 중 오류 발생`, error);
    }
  },

  /**
   * 풀 ID를 사용하여 풀의 현재 상태를 가져옵니다.
   * @param pool_id 풀 ID
   * @returns 풀 상태 정보
   */
  async getPoolStatus(pool_id: number | string): Promise<ApiResponse<{ status: string; occupancy: number }>> {
    try {
      console.log(`풀 ID(${pool_id})의 상태 정보 요청`);
      const response = await axios.get(`${API_URL}/api/pools/${pool_id}/status`);
      return handleApiResponse<{ status: string; occupancy: number }>(response);
    } catch (error) {
      return logApiError<{ status: string; occupancy: number }>(`풀 ID(${pool_id})의 상태 정보 조회 중 오류 발생`, error);
    }
  },

  /**
   * 풀 ID를 사용하여 풀의 안전 상태를 가져옵니다.
   * @param pool_id 풀 ID
   * @returns 풀 안전 상태 정보
   */
  async getPoolSafetyStatus(pool_id: number | string): Promise<ApiResponse<SafetyStatus>> {
    try {
      console.log(`풀 ID(${pool_id})의 안전 상태 정보 요청`);
      const response = await axios.get(`${API_URL}/api/pools/${pool_id}/safety`);
      return handleApiResponse<SafetyStatus>(response);
    } catch (error) {
      return logApiError<SafetyStatus>(`풀 ID(${pool_id})의 안전 상태 정보 조회 중 오류 발생`, error);
    }
  },

  /**
   * 풀 ID를 사용하여 풀 비밀번호를 검증합니다.
   * @param pool_id 풀 ID
   * @param password 검증할 비밀번호
   * @returns 검증 결과
   */
  async verifyPoolPasswordById(
    pool_id: number | string,
    password: string
  ): Promise<
    ApiResponse<{
      verified: boolean;
      message: string;
      access_token?: string;
      pool_id?: number;
    }>
  > {
    try {
      console.log(`풀 ID(${pool_id})의 비밀번호 검증 요청`);
      
      // 풀 ID를 코드로 변환
      let poolId = pool_id;
      if (typeof pool_id === 'string') {
        const poolInfo = await this.getPoolByCode(pool_id);
        if (!poolInfo.success || !poolInfo.data?.id) {
          throw new Error('풀 ID를 찾기 불가');
        }
        poolId = poolInfo.data.id;
      }
      
      // 서버에서 제공하는 올바른 비밀번호 확인 엔드포인트 사용
      console.log(`API 요청 URL: ${API_URL}/api/pools/${poolId}/password/verify`);
      
      try {
        const response = await axios.post(`${API_URL}/api/pools/${poolId}/password/verify`, {
          password,
        });
        console.log('비밀번호 검증 응답:', response.data);
        const d = response.data as {
          success?: boolean;
          verified?: boolean;
          message?: string;
          access_token?: string;
          pool_id?: number;
        };
        const ok = Boolean(d.success ?? d.verified);
        return {
          success: ok,
          message: d.message || (ok ? '비밀번호가 일치' : '비밀번호 검증 실패'),
          data: {
            verified: ok,
            message: d.message || '',
            access_token: d.access_token,
            pool_id: d.pool_id ?? Number(poolId),
          },
        };
      } catch (apiError) {
        console.error('비밀번호 검증 엔드포인트 에러:', apiError);
        throw apiError;
      }
    } catch (error) {
      return logApiError<{
        verified: boolean;
        message: string;
        access_token?: string;
        pool_id?: number;
      }>(`풀 ID(${pool_id})의 비밀번호 검증 중 오류 발생`, error);
    }
  }
};

export default standardPoolApi;

// 이전 named export를 위한 호환성 계층
export { standardPoolApi }; 